import {
  CARD_FOIL_IDS,
  asCardFoil,
  type CardFoilId,
} from '../types/foil'
import type { PackId } from '../packs/ids'

export type FoilCounts = Partial<Record<CardFoilId, number>>

export type CopiesMap = Record<string, FoilCounts>

/** Unix ms when the player last gained a copy of this card id. */
export type AcquiredAtMap = Record<string, number>

export type CollectionState = {
  copies: CopiesMap
  counts: Record<string, number>
  packs: Record<PackId, number>
  preferredFoil: Record<string, CardFoilId>
  /** Last time a copy of each owned card was added. Missing = unknown/legacy. */
  acquiredAt: AcquiredAtMap
  /**
   * Cards and foils ever obtained. Presence, not a playable count.
   * Missing on older payloads; treat as empty and fall back to copies.
   */
  album?: AlbumMap
  welcomePending: boolean
}

/** Foil presence for the collection album. Counts are always 1. */
export type AlbumMap = CopiesMap

/**
 * Presence map: each foil the player has at least one of becomes 1.
 */
export function albumPresence(copies: CopiesMap): AlbumMap {
  const next: AlbumMap = {}
  for (const [id, foils] of Object.entries(copies)) {
    const bucket: FoilCounts = {}
    for (const [foil, n] of Object.entries(foils ?? {})) {
      if ((n ?? 0) > 0) bucket[asCardFoil(foil)] = 1
    }
    if (Object.keys(bucket).length > 0) next[id] = bucket
  }
  return next
}

/** Union of two presence maps. Does not drop foils that copies no longer hold. */
export function mergeAlbum(album: AlbumMap, copies: CopiesMap): AlbumMap {
  const next: AlbumMap = albumPresence(album)
  for (const [id, foils] of Object.entries(albumPresence(copies))) {
    next[id] = { ...(next[id] ?? {}), ...foils }
  }
  return next
}

const DEVTOOLS_COPIES_PER_FOIL = 3

/**
 * Copies of one house foil for a card.
 */
export function foilCount(
  copies: CopiesMap,
  cardId: string | number,
  foil: CardFoilId,
): number {
  return copies[String(cardId)]?.[foil] ?? 0
}

/**
 * Total owned copies of a card across foils.
 */
export function ownedCount(copies: CopiesMap, cardId: string | number): number {
  const foils = copies[String(cardId)]
  if (!foils) return 0
  let total = 0
  for (const n of Object.values(foils)) total += n ?? 0
  return total
}

/**
 * Card id → total copies, omitting zeros.
 */
export function countsFromCopies(copies: CopiesMap): Record<string, number> {
  const next: Record<string, number> = {}
  for (const [id, foils] of Object.entries(copies)) {
    let total = 0
    for (const n of Object.values(foils ?? {})) total += n ?? 0
    if (total > 0) next[id] = total
  }
  return next
}

/**
 * True when this deck's foil copies fit in the album.
 */
export function deckIsOwned(
  copies: CopiesMap,
  main: string[],
  extra: string[],
  mainFoils?: string[],
  extraFoils?: string[],
): boolean {
  const ids = [...main, ...extra]
  const foils = [...(mainFoils ?? []), ...(extraFoils ?? [])]
  if (foils.length === ids.length && foils.length > 0) {
    const used: Record<string, FoilCounts> = {}
    for (let i = 0; i < ids.length; i++) {
      const id = ids[i] ?? ''
      const foil = asCardFoil(foils[i])
      const bucket = used[id] ?? {}
      const next = (bucket[foil] ?? 0) + 1
      if (next > foilCount(copies, id, foil)) return false
      used[id] = { ...bucket, [foil]: next }
    }
    return true
  }
  const used: Record<string, number> = {}
  for (const id of ids) {
    used[id] = (used[id] ?? 0) + 1
    if (used[id] > ownedCount(copies, id)) return false
  }
  return true
}

/**
 * Adds one print. Does not mutate `copies`.
 */
export function addCopy(
  copies: CopiesMap,
  cardId: string,
  foil: CardFoilId,
): CopiesMap {
  const current = copies[cardId] ?? {}
  return {
    ...copies,
    [cardId]: { ...current, [foil]: (current[foil] ?? 0) + 1 },
  }
}

/**
 * Last-acquired unix ms for a card, or 0 when the date was never stored.
 */
export function acquiredAtMs(
  map: AcquiredAtMap,
  cardId: string | number,
): number {
  return map[String(cardId)] ?? 0
}

/**
 * Drops unknown ids and non-positive timestamps. Owned cards without a
 * stamp stay missing so they sort as oldest.
 */
export function sanitizeAcquiredAt(
  raw: unknown,
  copies: CopiesMap,
): AcquiredAtMap {
  if (!raw || typeof raw !== 'object') return {}
  const next: AcquiredAtMap = {}
  for (const [id, value] of Object.entries(raw as Record<string, unknown>)) {
    if (ownedCount(copies, id) < 1) continue
    const ms = typeof value === 'number' ? value : Number(value)
    if (!Number.isFinite(ms) || ms < 1) continue
    next[id] = Math.floor(ms)
  }
  return next
}

/**
 * Stamps `now` on every card whose total copies increased.
 */
export function stampAcquiredAt(
  map: AcquiredAtMap,
  prev: CopiesMap,
  nextCopies: CopiesMap,
  now = Date.now(),
): AcquiredAtMap {
  const stamped: AcquiredAtMap = { ...map }
  for (const id of Object.keys(nextCopies)) {
    if (ownedCount(nextCopies, id) > ownedCount(prev, id)) {
      stamped[id] = now
    }
  }
  for (const id of Object.keys(stamped)) {
    if (ownedCount(nextCopies, id) < 1) delete stamped[id]
  }
  return stamped
}

/**
 * Drops unknown foils and non-positive counts.
 */
export function sanitizeCopies(raw: unknown): CopiesMap {
  if (!raw || typeof raw !== 'object') return {}
  const next: CopiesMap = {}
  for (const [id, foils] of Object.entries(raw as Record<string, unknown>)) {
    if (!id || !foils || typeof foils !== 'object') continue
    const bucket: FoilCounts = {}
    for (const [key, n] of Object.entries(foils as Record<string, unknown>)) {
      if (!Number.isInteger(n) || (n as number) < 1) continue
      const foil = asCardFoil(key)
      bucket[foil] = (bucket[foil] ?? 0) + (n as number)
    }
    if (Object.keys(bucket).length > 0) next[id] = bucket
  }
  return next
}

/**
 * Album display choice per card, only when that foil is still owned.
 */
export function sanitizePreferredFoil(
  raw: unknown,
  copies: CopiesMap,
  album?: AlbumMap,
): Record<string, CardFoilId> {
  if (!raw || typeof raw !== 'object') return {}
  const next: Record<string, CardFoilId> = {}
  for (const [id, value] of Object.entries(raw as Record<string, unknown>)) {
    const foil = asCardFoil(value)
    const inCopies = foilCount(copies, id, foil) >= 1
    const inAlbum = album ? foilCount(album, id, foil) >= 1 : false
    if (!inCopies && !inAlbum) continue
    next[id] = foil
  }
  return next
}

/**
 * At least `DEVTOOLS_COPIES_PER_FOIL` of every house foil for each catalog id.
 */
export function grantAllPrints(
  copies: CopiesMap,
  catalogIds: readonly string[],
): CopiesMap {
  const next: CopiesMap = { ...copies }
  for (const id of catalogIds) {
    const current = next[id] ?? {}
    const foils: FoilCounts = { ...current }
    for (const foil of CARD_FOIL_IDS) {
      foils[foil] = Math.max(foils[foil] ?? 0, DEVTOOLS_COPIES_PER_FOIL)
    }
    next[id] = foils
  }
  return next
}

export { DEVTOOLS_COPIES_PER_FOIL }
